import axios from 'axios';
import config from 'config';
import { isEmpty } from 'lodash';

import {
    ConfigsService as GeneralConfigsService,
    HTTPService,
    UrlService,
    WindowProxy,
    LocaleService,
} from '@sm360/widget-commons-core-services';
import { LeadSubmissionBuilder, LEAD_TYPES } from '@sm360/widget-commons-leads';
import { CookieService, COOKIES, UtilsService } from '@sm360/widget-commons-general';

import TokenService from 'login/services/TokenService/TokenService.js';
import URL_PARAMS from 'login/constants/urlParams.js';

class AuthenticationService {
    constructor() {
        this.accountBasePath = `${config.endpoint.account}/accounts`;
    }

    sendPasswordResetCode(email) {
        const basePath = `${this.accountBasePath}/passwords/emails`;
        const usernameParam = `username=${encodeURIComponent(email)}`;
        const organizationIdParam = `organizationId=${GeneralConfigsService.getOrganizationId()}`;
        const organizationUnitIdParam = `organizationUnitId=${GeneralConfigsService.getOrganizationUnitId()}`;
        const url = `${basePath}?${usernameParam}&${organizationIdParam}&${organizationUnitIdParam}`;

        return axios.post(url);
    }

    authenticate(params) {
        const basePath = `${this.accountBasePath}/authenticate`;
        const usernameParam = `username=${encodeURIComponent(params.username)}`;
        const organizationIdParam = `organizationId=${GeneralConfigsService.getOrganizationId()}`;
        const organizationUnitIdParam = `organizationUnitId=${GeneralConfigsService.getOrganizationUnitId()}`;
        const url = `${basePath}?${usernameParam}&${organizationIdParam}&${organizationUnitIdParam}`;

        return axios
            .post(url, params.password, {
                headers: {
                    'Content-Type': 'text/plain',
                },
            })
            .then((response) => {
                if (!response || response.status === 204) {
                    return Promise.reject(HTTPService.formatRejectResponse('authenticateError', response));
                }

                const {
                    accountCreation,
                    username,
                    firstname,
                    lastname,
                    homePhone,
                    contactId,
                    optInEmail,
                    quote,
                } = params;

                if (accountCreation) {
                    if (quote && !isEmpty(quote.id)) {
                        this.createAccountCreationLead(
                            username,
                            firstname,
                            lastname,
                            homePhone,
                            contactId,
                            optInEmail,
                            quote
                        );
                    } else {
                        this.createAccountCreationLead(
                            username,
                            firstname,
                            lastname,
                            homePhone,
                            contactId,
                            optInEmail,
                            {}
                        );
                    }
                } else if (quote && !isEmpty(quote.id)) {
                    this.createAccountConnectionLead(username, quote);
                } else {
                    this.createAccountConnectionLead(username, {});
                }

                TokenService.setUserToken(response.data.token, response.data.refreshToken);
                return Promise.resolve(response);
            });
    }

    resetPassword(password) {
        const resetCode = UrlService.getUrlParameter(URL_PARAMS.RESET_CODE);
        const params = {
            resetCode,
            newPassword: password,
        };

        return axios.put(`${this.accountBasePath}/passwords`, params);
    }

    createAccountCreationLead(email, firstname, lastname, homePhone, contactId, optInEmail, quote) {
        return new LeadSubmissionBuilder()
            .addLeadType(LEAD_TYPES.ACCOUNT_CREATION)
            .addContactInformation(firstname, lastname, homePhone, email, contactId)
            .setIsAccountCreation()
            .addOptInEmail(optInEmail)
            .addQuote(quote)
            .submit();
    }

    createAccountConnectionLead(email, quote) {
        return new LeadSubmissionBuilder()
            .addLeadType(LEAD_TYPES.ACCOUNT_CREATION)
            .addContactInformation(undefined, undefined, undefined, email, undefined)
            .addQuote(quote)
            .submit();
    }

    createAccountAndAuthenticate(accountCreationInfo, quote) {
        const splitName = UtilsService.splitName(accountCreationInfo.fullname);
        const account = {
            organizationUnitId: GeneralConfigsService.getOrganizationUnitId(),
            organizationId: GeneralConfigsService.getOrganizationId(),
            username: accountCreationInfo.email,
            password: accountCreationInfo.password,
            firstname: splitName.firstname,
            language: LocaleService.getSimpleLocale(),
            lastname: splitName.lastname,
            email1: accountCreationInfo.email,
            homePhone: accountCreationInfo.phoneNumber,
            sexId: 0,
            optInEmail: accountCreationInfo.receivePromotionalEmail,
            optInSms: true,
            optInPhone: true,
        };
        return this.createAccount(account).then((contactId) => {
            this.authenticate({
                username: account.email1,
                password: account.password,
                firstname: account.firstname,
                lastname: account.lastname,
                homePhone: account.homePhone,
                optInEmail: account.optInEmail,
                accountCreation: true,
                contactId,
                quote,
            });
        });
    }

    /**
     * Create a new account and submit a lead of type ACCOUNT_CREATION
     * @param {String} accountCreationInfo.fullname
     * @param {String} accountCreationInfo.email
     * @param {String} accountCreationInfo.password
     * @param {String} accountCreationInfo.phoneNumber
     * @param {Boolean} accountCreationInfo.receivePromotionalEmail
     */
    createAccount(account) {
        return axios
            .post(`${this.accountBasePath}/v2?emailValidation=true`, account)
            .then(({ data: { contactId } }) => {
                return contactId;
            });
    }

    logout() {
        CookieService.deleteToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
        CookieService.deleteToken(COOKIES.ACCOUNT.REFRESH_TOKEN);
        WindowProxy.localStorage().removeItem('inventoryCheckout.quote'); // TODO set constant when moved

        WindowProxy.location().assign('/');
    }
}

export default new AuthenticationService();



// WEBPACK FOOTER //
// ./src/services/AuthenticationService/AuthenticationService.js